import { IsolatedCartType, getBuyNowKey, getCartToken } from '@teko/cart/v2';
import { logger } from '@teko/log';
import {
  AddToCartType,
  TrackAddToCartParams,
  trackAddToCart,
} from '@teko/tracking';
import { ProductCardProps } from '@teko/types/ecommerce';
import TerraKit from '@terra-js/terra-kit';
import { CartType } from '@terra-js/terra-kit/dist/common/CommonKitModule';
import { useCartV2, useTenantConfig, useToast } from 'contexts';
import terraHelper from 'helpers/terra';
import { t } from 'i18n';
import { useRouter } from 'next/router';

const SINGLE_PRODUCT_QUANTITY = 1;

export const useActionButtons = () => {
  const { allowBuyNow } = useTenantConfig();
  const { onAddSingleItem } = useCartV2();
  const router = useRouter();
  const toast = useToast();

  const handleAddToCart = async (
    product: ProductCardProps,
    type = AddToCartType.ADDTOCART,
  ) => {
    const trackAddToCartParams: TrackAddToCartParams = {
      product: {
        sku: product.sellerSku || '',
        name: product.name || '',
        price: product.price?.latestPrice as number,
        brand: {
          name: product.brand?.name || '',
        },
        addToCartPrice: product.price?.supplierRetailPrice || 0,
        sellerSku: product.sellerSku,
      },
      selectedPromotionId: product.selectPromotionId,
      type,
    };

    try {
      const res = await onAddSingleItem(
        {
          sku: product.sku || '',
          sellerSku: product.sellerSku,
          quantity: SINGLE_PRODUCT_QUANTITY,
          promotionId:
            product.selectPromotionId !== 0
              ? product.selectPromotionId
              : undefined,
        },
        true,
        allowBuyNow && type === AddToCartType.BUYNOW
          ? IsolatedCartType.BUY_NOW
          : undefined,
      );

      if (res) {
        trackAddToCart({
          ...trackAddToCartParams,
          status: 'success',
          cartId: res.id,
        });
      } else {
        trackAddToCart({ ...trackAddToCartParams, status: 'failed' });
      }

      if (
        res &&
        (type === AddToCartType.BUYNOW || type === AddToCartType.INSTALLMENT)
      ) {
        const cartToken = getCartToken();
        if (type === AddToCartType.BUYNOW && !allowBuyNow) {
          // if open inside a webview on mobile app
          if (!!TerraKit.isAvailable()) {
            if (!!cartToken) {
              TerraKit.order().navigateToCart(cartToken);
            } else {
              toast?.error?.({
                title: t('Error'),
                message: t('Không lấy được cart token. Vui lòng thử lại!'),
              });
            }
          } else {
            router.push('/cart');
          }
        } else {
          // if open inside a webview on mobile app
          if (!!TerraKit.isAvailable()) {
            if (!!cartToken) {
              terraHelper.observeOpenBuyNow({
                token: cartToken,
                sku: product.sku || '',
                quantity: SINGLE_PRODUCT_QUANTITY,
                cartType: CartType.BUY_NOW,
              });
            } else {
              toast?.error?.({
                title: t('Error'),
                message: t('Không lấy được cart token. Vui lòng thử lại!'),
              });
            }
          } else {
            const key = getBuyNowKey(true);
            router.push(`/checkout?bnk=${key}`);
          }
        }
      }
    } catch (error) {
      trackAddToCart({ ...trackAddToCartParams, status: 'failed' });
      logger.error(error);
    }
  };

  const buyNow = async (product: ProductCardProps) => {
    return handleAddToCart(product, AddToCartType.BUYNOW);
  };

  const addToCart = async (product: ProductCardProps) => {
    return handleAddToCart(product, AddToCartType.ADDTOCART);
  };

  return { buyNow, addToCart };
};
